import type { ChannelRef } from '../catalog/channel';
import { CONTROL_KIND } from '../catalog/control';
import { LOAD_TYPE_SPECS } from '../catalog/loadType';
import { RCU_COMMAND, TRAVEL } from '../catalog/protocol';
import type { TravelKind } from '../catalog/protocol';
import { POWER_STATE } from '../../device/catalog';
import type { Load, WiredLoad } from '../model';

interface RcuCommandBase {
  readonly load: Load;
  /** Resolved at build time, so a command can only exist for a wired load. */
  readonly channel: ChannelRef;
}

/**
 * A guest action aimed at one physical output. Levels arrive in percentage terms
 * (0–100); turning them into hardware values is the payload builder's job, so
 * nothing that talks to the RCU has to know the module's scale.
 */
export type RcuCommand =
  | (RcuCommandBase & { readonly kind: typeof RCU_COMMAND.SET_POWER; readonly on: boolean })
  | (RcuCommandBase & {
      readonly kind: typeof RCU_COMMAND.SET_LEVEL;
      readonly level: number;
      /** A dimmer at zero is off, so power travels with the level. */
      readonly on: boolean;
    })
  | (RcuCommandBase & {
      readonly kind: typeof RCU_COMMAND.SET_TRAVEL;
      readonly travel: TravelKind;
    });

/** Output that is either off or on: onboard relays and DIN relay modules. */
export const setPower = (load: WiredLoad, on: boolean): RcuCommand => ({
  kind: RCU_COMMAND.SET_POWER,
  load,
  channel: load.channel,
  on,
});

/** Dimmer output. `on` false sends the lowest value rather than a separate off. */
export const setLevel = (load: WiredLoad, level: number, on: boolean): RcuCommand => ({
  kind: RCU_COMMAND.SET_LEVEL,
  load,
  channel: load.channel,
  level,
  on,
});

/**
 * The command a switch sends, for the state the switch is showing.
 *
 * A dimmer keeps a level while it is off, so switching one on has to send the
 * level the guest can see — which is the device's own report when there is one,
 * not necessarily this tablet's last guess. Passing the displayed level in is
 * what keeps the screen and the command from disagreeing.
 */
export const toggleCommand = (load: WiredLoad, level: number, on: boolean): RcuCommand =>
  LOAD_TYPE_SPECS[load.type].control === CONTROL_KIND.LEVEL
    ? setLevel(load, level, on)
    : setPower(load, on);

/**
 * Motor output. There is no absolute position to ask for: a curtain module is
 * told which way to run, or that it should stop.
 */
export const setTravel = (load: WiredLoad, travel: TravelKind): RcuCommand => ({
  kind: RCU_COMMAND.SET_TRAVEL,
  load,
  channel: load.channel,
  travel,
});

/** Labels for the travel buttons, in the order the guest control shows them. */
export const TRAVEL_BUTTONS = [
  { travel: TRAVEL.OPEN, label: 'Open' },
  { travel: TRAVEL.STOP, label: 'Stop' },
  { travel: TRAVEL.CLOSE, label: 'Close' },
] as const satisfies readonly { readonly travel: TravelKind; readonly label: string }[];

/** Human-readable summary, used in logs and messages. */
export const describeCommand = (command: RcuCommand): string => {
  switch (command.kind) {
    case RCU_COMMAND.SET_POWER:
      return command.on ? POWER_STATE.ON : POWER_STATE.OFF;
    case RCU_COMMAND.SET_LEVEL:
      return command.on ? `level ${command.level}%` : POWER_STATE.OFF;
    case RCU_COMMAND.SET_TRAVEL:
      return command.travel;
  }
};
